import { apiClient, unwrap, unwrapEnvelope } from './client';
import type { ApiSuccess, ProductFilters, PaginationMeta } from '@/types/api';
import type { Product } from '@/types/product';

export interface ProductListResult {
  items: Product[];
  pagination: PaginationMeta;
}

function toQuery(filters: ProductFilters): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (filters.page !== undefined) out.page = filters.page;
  if (filters.limit !== undefined) out.limit = filters.limit;
  if (filters.categoryId !== undefined) out.categoryId = filters.categoryId;
  if (filters.categorySlug) out.categorySlug = filters.categorySlug;
  if (filters.q) out.q = filters.q;
  if (filters.minPrice !== undefined) out.minPrice = filters.minPrice;
  if (filters.maxPrice !== undefined) out.maxPrice = filters.maxPrice;
  if (filters.size?.length) out.size = filters.size.join(',');
  if (filters.color?.length) out.color = filters.color.join(',');
  if (filters.isFeatured !== undefined) out.isFeatured = filters.isFeatured;
  if (filters.sort) out.sort = filters.sort;
  return out;
}

export const productsApi = {
  async list(filters: ProductFilters = {}): Promise<ProductListResult> {
    const res = await apiClient.get<ApiSuccess<Product[]>>('/products', {
      params: toQuery(filters),
    });
    const env = unwrapEnvelope(res);
    return {
      items: env.data,
      pagination: env.pagination ?? {
        page: 1,
        limit: env.data.length,
        total: env.data.length,
        totalPages: 1,
        nextPage: null,
      },
    };
  },

  async featured(limit = 8): Promise<Product[]> {
    const res = await apiClient.get<ApiSuccess<Product[]>>('/products/featured', {
      params: { limit },
    });
    return unwrap(res);
  },

  async search(query: string, page = 1, limit = 20): Promise<ProductListResult> {
    const res = await apiClient.get<ApiSuccess<Product[]>>('/products/search', {
      params: { q: query, page, limit },
    });
    const env = unwrapEnvelope(res);
    return {
      items: env.data,
      pagination: env.pagination ?? {
        page,
        limit,
        total: env.data.length,
        totalPages: 1,
        nextPage: null,
      },
    };
  },

  async getBySlug(slug: string): Promise<Product> {
    const res = await apiClient.get<ApiSuccess<Product>>(
      `/products/${encodeURIComponent(slug)}`
    );
    return unwrap(res);
  },
};
